/**
 * Model analizi — Çalıştır / Canlıya Al yanıtı ve geçmiş kayıtları doğrudan
 * buraya çizilir.
 *
 * Okuma notları (backend sözleşmesinden):
 *   - `toplam_talep` plan ufkundaki TOPLAM talep; `toplam_uretim`'den büyük
 *     olması normal. İkisi yan yana oran gibi gösterilmez, açığın ölçüsü
 *     `toplam_acik`.
 *   - `kayip_dakika` null gelebilir (renk matrisinde tanımsız geçiş) → "–".
 *   - Renk değişiminde `onceki_sira` ile `sira` ardışık olmayabilir; aradaki
 *     duruş satırları atlanıyor.
 */
import { Empty, Table, Tabs, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { sayiYaz as sayi } from "./bicim";
import "./Simulation.css";

/** Açık veren satırlar vurgulanır. */
const acikSinifi = (r) => (Number(r.acik) > 0 ? "sim-acik-satir" : "");

function OzetKarti({ etiket, deger, vurgu }) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        vurgu ? "border-state-bad/40 bg-state-bad/10" : "border-line bg-surface"
      }`}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-content-faint">
        {etiket}
      </div>
      <div
        className={`mt-0.5 text-[20px] font-semibold leading-tight ${
          vurgu ? "text-state-bad" : "text-content"
        }`}
      >
        {deger}
      </div>
    </div>
  );
}

export default function AnalizPaneli({ sonuc, baslik }) {
  const { t } = useTranslation();

  if (!sonuc) {
    return <Empty className="py-10" description={t("simulation.analysis.empty")} />;
  }

  const o = sonuc.ozet ?? {};
  const acikVar = Number(o.toplam_acik) > 0;
  const c = (k) => t(`simulation.analysis.cols.${k}`);

  const malzemeSutunlari = [
    { title: c("material"), dataIndex: "malzeme", width: 92 },
    { title: c("name"), dataIndex: "ad" },
    {
      title: c("side"),
      dataIndex: "taraf",
      width: 72,
      render: (v) =>
        v === "govde_on"
          ? t("simulation.cols.frontBody")
          : v === "govde"
          ? t("simulation.cols.body")
          : v,
    },
    { title: t("simulation.cols.color"), dataIndex: "renk", width: 72 },
  ];

  const sekmeler = [
    {
      key: "acik",
      label: t("simulation.analysis.tabs.shortage", {
        n: sonuc.acik_malzeme_bazinda?.length ?? 0,
      }),
      children: (
        <Table
          className="sim-tablo"
          size="small"
          rowKey={(r) => `${r.malzeme}-${r.taraf}`}
          dataSource={sonuc.acik_malzeme_bazinda ?? []}
          pagination={false}
          rowClassName={acikSinifi}
          locale={{ emptyText: t("simulation.analysis.noShortageMaterial") }}
          columns={[
            ...malzemeSutunlari,
            { title: c("shortage"), dataIndex: "acik", align: "right", width: 60, render: (v) => sayi(v) },
          ]}
        />
      ),
    },
    {
      key: "detay",
      label: t("simulation.analysis.tabs.detail", { n: sonuc.acik_detay?.length ?? 0 }),
      children: (
        <Table
          className="sim-tablo"
          size="small"
          rowKey={(r, i) => `${r.malzeme}-${r.sira}-${i}`}
          dataSource={sonuc.acik_detay ?? []}
          pagination={{ pageSize: 20, size: "small", hideOnSinglePage: true }}
          rowClassName={acikSinifi}
          locale={{ emptyText: t("simulation.analysis.noShortage") }}
          scroll={{ x: 720 }}
          columns={[
            { title: t("simulation.cols.seq"), dataIndex: "sira", width: 60 },
            { title: c("date"), dataIndex: "tarih", width: 100 },
            { title: c("hour"), dataIndex: "saat", width: 64 },
            ...malzemeSutunlari,
            { title: c("demand"), dataIndex: "talep", align: "right", width: 72, render: (v) => sayi(v) },
            { title: c("met"), dataIndex: "karsilanan", align: "right", width: 90, render: (v) => sayi(v) },
            { title: c("shortage"), dataIndex: "acik", align: "right", width: 64, render: (v) => sayi(v) },
          ]}
        />
      ),
    },
    {
      key: "renk",
      label: t("simulation.analysis.tabs.color", { n: sonuc.renk_degisimleri?.length ?? 0 }),
      children: (
        <Table
          className="sim-tablo"
          size="small"
          rowKey={(r) => `${r.onceki_sira}-${r.sira}`}
          dataSource={sonuc.renk_degisimleri ?? []}
          pagination={false}
          locale={{ emptyText: t("simulation.analysis.noColorChange") }}
          columns={[
            {
              title: t("simulation.cols.seq"),
              key: "sira",
              width: 110,
              // Ardışık değilse arada duruş satırları var.
              render: (_, r) => `${r.onceki_sira} → ${r.sira}`,
            },
            {
              title: c("transition"),
              key: "gecis",
              render: (_, r) => (
                <span>
                  <Tag>{r.onceki_renk}</Tag>→ <Tag color="gold">{r.yeni_renk}</Tag>
                </span>
              ),
            },
            {
              title: c("lossMin"),
              dataIndex: "kayip_dakika",
              align: "right",
              width: 90,
              // Renk matrisinde tanımsız geçişte null geliyor.
              render: (v) => sayi(v, 1),
            },
          ]}
        />
      ),
    },
    {
      key: "stok",
      label: t("simulation.analysis.tabs.endStock"),
      children: (
        <Table
          className="sim-tablo"
          size="small"
          rowKey="malzeme"
          dataSource={sonuc.donem_sonu_stok ?? []}
          pagination={false}
          columns={[
            { title: c("material"), dataIndex: "malzeme", width: 92 },
            { title: c("name"), dataIndex: "ad" },
            { title: c("stock"), dataIndex: "stok", align: "right", width: 72, render: (v) => sayi(v) },
          ]}
        />
      ),
    },
  ];

  const a = (k) => t(`simulation.analysis.${k}`);

  return (
    <div className="flex flex-col gap-3">
      {baslik && <div className="text-[13px] font-semibold text-content">{baslik}</div>}

      <div className="text-[12px] text-content-muted">
        {a("evaluatedPlan")} <b className="text-content">{sonuc.degerlendirilen_plan ?? "–"}</b>
        {" · "}
        {a("sourcePlan")} <b className="text-content">{sonuc.kaynak_plan ?? "–"}</b>
      </div>

      {/* Toplam açık en önde: talep/üretim kıyası değil, açık esas alınır. */}
      <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
        <OzetKarti etiket={a("totalShortage")} deger={sayi(o.toplam_acik)} vurgu={acikVar} />
        <OzetKarti
          etiket={a("shortageMaterials")}
          deger={sayi(o.acik_veren_malzeme_sayisi)}
          vurgu={acikVar}
        />
        <OzetKarti etiket={a("totalProduction")} deger={sayi(o.toplam_uretim)} />
        <OzetKarti etiket={a("periods")} deger={sayi(o.periyot)} />
        <OzetKarti etiket={a("productionPeriods")} deger={sayi(o.uretim_yapilan_periyot)} />
        <OzetKarti etiket={a("stopPeriods")} deger={sayi(o.durus_periyodu)} />
        <OzetKarti etiket={a("colorChanges")} deger={sayi(o.renk_degisim_sayisi)} />
        <OzetKarti etiket={a("colorChangeLoss")} deger={sayi(o.renk_degisim_kaybi_dakika, 1)} />
        <OzetKarti etiket={a("horizonDemand")} deger={sayi(o.toplam_talep)} />
      </div>

      <Tabs className="sim-sekmeler" size="small" items={sekmeler} />
    </div>
  );
}
